import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pressingFetch, getPresserSession, clearPressingSession, useAuth, authenticatedFetch } from './AuthComponent';
import { useStore, STORE_DISPLAY_NAMES } from './StoreContext';
import { playSuccess, playError, preloadSounds } from './sounds';
import CameraScanner from './CameraScanner';
import { getPreviewImageUrls, shopifyResize } from './previewImage';

/**
 * Store Pickup Portal — iPhone-sized kiosk for retail store associates.
 *
 *   Receive Orders mode  → scan box of incoming orders, fire Shopify
 *                          "ready for pickup" notifications. Mismatch modal
 *                          if scanned order's pickup location doesn't match
 *                          the logged-in store.
 *
 *   Order Pickup mode    → customer arrives. Scan, finger-draw signature,
 *                          type customer name, submit. Backend marks the order
 *                          picked-up in Shopify, attaches the signature image to
 *                          Shopify's order event log, and adds a timeline event
 *                          in Operations Hub.
 */
export default function StorePickupPortal({ consoleMode = false }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { activeStore } = useStore();
    const [pinSession, setPinSession] = useState(() => getPresserSession());
    const [mode, setMode] = useState('menu');  // 'menu' | 'receive' | 'pickup'
    // Console-mode location picker — shows EVERY location the user can stand
    // at (admins: all active locations across stores; everyone else: their
    // own operations_user_locations assignments). User picks the physical
    // pickup location they're currently at. Subsequent scans filter to that
    // single (store_key, shopify_location_id) so wrong-store orders trigger
    // the mismatch modal correctly.
    const [chosenLocation, setChosenLocation] = useState(null);
    // PIN-mode store picker (2026-08-27): a portal user assigned to MULTIPLE
    // stores must declare which one they are physically at right after PIN
    // entry — scans then narrow to that store server-side, so wrong-store
    // orders trigger the mismatch modal instead of silently matching one of
    // their other stores.
    const [pinLoc, setPinLoc] = useState(null);
    const [pickerLocations, setPickerLocations] = useState(null);  // null = loading
    // Console picker search (2026-09-09, Eric: "a filter at the top where I
    // can filter a specific store instead of scrolling down"). Matches on the
    // location name or the store name; Enter picks the only remaining match.
    const [locFilter, setLocFilter] = useState('');
    useEffect(() => {
        if (!consoleMode) { setPickerLocations([]); return; }
        let cancelled = false;
        authenticatedFetch('/api/store-pickup/console-locations')
            .then(r => r.ok ? r.json() : { locations: [] })
            .then(data => { if (!cancelled) setPickerLocations(data.locations || []); })
            .catch(() => { if (!cancelled) setPickerLocations([]); });
        return () => { cancelled = true; };
    }, [consoleMode]);

    // Auto-select when the user has access to exactly one pickup location.
    // Skips the picker entirely so single-location users don't have to tap
    // through a one-option list every time they enter the portal.
    useEffect(() => {
        if (!consoleMode || chosenLocation) return;
        if (!Array.isArray(pickerLocations)) return;
        const valid = pickerLocations.filter(l => l.shopify_location_id && l.store_key);
        if (valid.length === 1) setChosenLocation(valid[0]);
    }, [consoleMode, pickerLocations, chosenLocation]);

    useEffect(() => {
        // Console mode launches from the sidebar — keep the sidebar/AppShell
        // visible by NOT overriding the document body background. PIN-mode is
        // a fullscreen kiosk takeover; body bg matches the new light page bg
        // so the kiosk theme is consistent with the Picking/Pressing/Packing
        // portals once the PIN screen has unlocked.
        if (consoleMode) return;
        document.body.style.backgroundColor = '#f1f2f4';
        return () => { document.body.style.backgroundColor = ''; };
    }, [consoleMode]);

    useEffect(() => { preloadSounds(); }, []);

    useEffect(() => {
        if (consoleMode) return;  // PIN gate doesn't apply
        const s = getPresserSession();
        if (!s || s.portal_type !== 'store') {
            navigate('/pressing-portal/login', { replace: true });
        } else {
            setPinSession(s);
        }
    }, [navigate, consoleMode]);

    // Build the active session: PIN-mode reads from sessionStorage; console-mode
    // synthesizes from the auth user filtered to the chosen single location.
    const session = (() => {
        if (consoleMode) {
            if (!user || !chosenLocation) return null;
            return {
                presser_id:                  0,
                first_name:                  user.first_name || user.username || 'User',
                portal_type:                 'store',
                assignments: [{
                    store_key:           chosenLocation.store_key,
                    location_id:         chosenLocation.id || chosenLocation.location_id,
                    shopify_location_id: chosenLocation.shopify_location_id,
                    location_name:       chosenLocation.name,
                    name:                chosenLocation.name,
                }],
                console_store_key:           chosenLocation.store_key,
                console_shopify_location_id: chosenLocation.shopify_location_id,
                consoleMode:                 true,
            };
        }
        if (!pinSession) return pinSession;
        const locs = pinSession.assignments || [];
        const chosen = locs.length === 1 ? locs[0] : pinLoc;
        if (chosen) {
            return {
                ...pinSession,
                assignments: [chosen],
                console_store_key:           chosen.store_key,
                console_shopify_location_id: chosen.shopify_location_id,
            };
        }
        return pinSession;
    })();

    const handleExit = () => {
        if (consoleMode) {
            navigate('/', { replace: true });
            return;
        }
        clearPressingSession();
        navigate('/pressing-portal/login', { replace: true });
    };

    // Console-mode location picker — show before the menu. The user picks the
    // physical pickup location they're currently standing at; the rest of the
    // session is built from that single assignment.
    if (consoleMode && !chosenLocation) {
        const userLocs = (pickerLocations || [])
            .filter(l => l.shopify_location_id && l.store_key)
            .sort((a, b) => {
                const ak = `${STORE_DISPLAY_NAMES[a.store_key] || a.store_key}|${a.name || ''}`;
                const bk = `${STORE_DISPLAY_NAMES[b.store_key] || b.store_key}|${b.name || ''}`;
                return ak.localeCompare(bk);
            })
            .filter(l => {
                const q = locFilter.trim().toLowerCase();
                if (!q) return true;
                const hay = `${l.name || ''} ${STORE_DISPLAY_NAMES[l.store_key] || l.store_key || ''}`.toLowerCase();
                return q.split(/\s+/).every(w => hay.includes(w));
            });
        const loading = pickerLocations === null;
        return (
            <div className={`spp-page${consoleMode ? ' spp-page--light' : ''}`}>
                <div className="spp-header">
                    {!consoleMode && (
                        <button className="spp-signout" onClick={() => navigate('/', { replace: true })}>↩ Sign Out</button>
                    )}
                    <div className="spp-title">Store Pickup</div>
                    <div className="spp-user">{user?.first_name || ''}</div>
                </div>
                <div className="spp-menu">
                    <div className="spp-store-chip">Which pickup location are you at?</div>
                    {!loading && (pickerLocations || []).length > 1 && (
                        <input
                            className="spp-loc-search"
                            type="search"
                            autoFocus
                            placeholder="Type to find a store…"
                            value={locFilter}
                            onChange={e => setLocFilter(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && userLocs.length === 1) setChosenLocation(userLocs[0]);
                                if (e.key === 'Escape') setLocFilter('');
                            }}
                            aria-label="Find a pickup location"
                        />
                    )}
                    {!loading && locFilter.trim() && userLocs.length === 0 && (pickerLocations || []).length > 0 && (
                        <div style={{ color: '#6d7175', textAlign: 'center', marginTop: 16, fontSize: 14 }}>
                            No location matches “{locFilter.trim()}”.
                        </div>
                    )}
                    {loading && (
                        <div style={{ color: '#6d7175', textAlign: 'center', marginTop: 16, fontSize: 14 }}>
                            Loading…
                        </div>
                    )}
                    {!loading && userLocs.length === 0 && !locFilter.trim() && (
                        <div style={{ color: '#6d7175', textAlign: 'center', marginTop: 16, fontSize: 14, padding: '0 24px' }}>
                            You don't have any pickup locations assigned. Ask an admin to add one in Settings → Locations.
                        </div>
                    )}
                    {userLocs.map(l => (
                        <button
                            key={`${l.store_key}_${l.shopify_location_id}`}
                            className="spp-tile spp-tile--receive"
                            onClick={() => setChosenLocation(l)}
                        >
                            <div className="spp-tile__icon">🏬</div>
                            <div className="spp-tile__label" style={{ textAlign: 'left' }}>
                                <div>{l.name}</div>
                            </div>
                        </button>
                    ))}
                </div>
                <SppStyles />
            </div>
        );
    }

    // PIN-mode multi-store picker — shown once, right after PIN entry.
    if (!consoleMode && pinSession && (pinSession.assignments || []).length > 1 && !pinLoc) {
        const locs = (pinSession.assignments || [])
            .filter(a => a.shopify_location_id)
            .sort((a, b) => (a.location_name || '').localeCompare(b.location_name || ''));
        return (
            <div className="spp-page">
                <div className="spp-header">
                    <button className="spp-signout" onClick={handleExit}>↩ Sign Out</button>
                    <div className="spp-title">Store Pickup</div>
                    <div className="spp-user">{pinSession.first_name}</div>
                </div>
                <div className="spp-menu">
                    <div className="spp-store-chip">Which store are you at?</div>
                    {locs.map(a => (
                        <button
                            key={`${a.store_key}_${a.shopify_location_id}`}
                            className="spp-tile spp-tile--receive"
                            onClick={() => setPinLoc(a)}
                        >
                            <div className="spp-tile__icon">🏬</div>
                            <div className="spp-tile__title">{a.location_name || a.name}</div>
                        </button>
                    ))}
                </div>
                <SppStyles />
            </div>
        );
    }

    if (!session) return null;

    const storeLocations = (session.assignments || []).map(a => ({
        store_key: a.store_key,
        location_id: a.location_id,
        shopify_location_id: a.shopify_location_id,
        name: a.location_name || a.name,
    }));

    const headerLocationName = consoleMode
        ? (chosenLocation?.name || '')
        : (session.assignments?.length === 1 ? session.assignments[0]?.location_name : '');

    return (
        <div className={`spp-page${consoleMode ? ' spp-page--light' : ''}`}>
            <div className="spp-header">
                {!consoleMode && (
                    <button className="spp-signout" onClick={handleExit}>↩ Sign Out</button>
                )}
                <div className="spp-header__title-block">
                    <div className="spp-title">Store Pickup</div>
                    {consoleMode && headerLocationName && (
                        <div className="spp-page-loc">{headerLocationName}</div>
                    )}
                </div>
                {!consoleMode && <div className="spp-user">{session.first_name}</div>}
            </div>
            {mode === 'menu' && <MenuScreen onPick={setMode} locations={storeLocations} />}
            {mode === 'receive' && <ReceiveOrdersScreen session={session} onBack={() => setMode('menu')} />}
            {mode === 'pickup' && <OrderPickupScreen session={session} onBack={() => setMode('menu')} />}
            <SppStyles />
        </div>
    );
}

function MenuScreen({ onPick, locations }) {
    const locLabel = locations.length === 0
        ? '— no store assignments —'
        : locations.length === 1
            ? locations[0].name
            : `${locations.length} stores`;
    return (
        <div className="spp-menu">
            <div className="spp-store-chip">{locLabel}</div>
            <button className="spp-tile spp-tile--receive" onClick={() => onPick('receive')}>
                <div className="spp-tile__icon">📦</div>
                <div className="spp-tile__title">Receive Orders</div>
                <div className="spp-tile__sub">Scan orders arriving for pickup</div>
            </button>
            <button className="spp-tile spp-tile--pickup" onClick={() => onPick('pickup')}>
                <div className="spp-tile__icon">🛍️</div>
                <div className="spp-tile__title">Order Pickup</div>
                <div className="spp-tile__sub">Customer arrived to collect</div>
            </button>
        </div>
    );
}

function ReceiveOrdersScreen({ session, onBack }) {
    const [scan, setScan] = useState('');
    const [busy, setBusy] = useState(false);
    const [feed, setFeed] = useState([]);  // {ts, name, status, message}
    const [mismatch, setMismatch] = useState(null);  // {name, correct_store}
    const [notTransferred, setNotTransferred] = useState(null);  // {name}
    const [editedNoPickup, setEditedNoPickup] = useState(null); // {name} — TP-edit child (:A) whose delivery method got flipped to SHIPPING
    const [alreadyDone, setAlreadyDone] = useState(null);  // {name, kind: 'picked_up'|'ready'|'fulfilled'}
    // 2026-09-02: override orders get no Shopify ready-for-pickup email — the operator must contact the customer.
    const [contactNeeded, setContactNeeded] = useState(null);  // {name, reason, original_store, override_store, customer_name, customer_email, customer_phone}
    const [cameraOpen, setCameraOpen] = useState(false);
    const inputRef = useRef(null);
    // Multi-FO picker: shown when the peek response says the order has 2+
    // open PICK_UP FOs at this store. Each row is a checkbox; already-ready
    // FOs are visible-but-disabled so the operator understands the full state.
    const [multiFoPeek, setMultiFoPeek] = useState(null); // {code, order_id, order_name, fulfillments:[]}
    const [multiFoSelected, setMultiFoSelected] = useState([]);   // fulfillment_order_id[]

    const focusScan = useCallback(() => {
        // small delay so iOS Safari accepts focus after the modal closes
        setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 50);
    }, []);

    useEffect(() => { focusScan(); }, [focusScan]);

    const submit = async (raw, fulfillmentOrderIds = null, confirmTransfer = false) => {
        const code = (raw || '').trim();
        if (!code) return;
        setScan('');
        if (busy) return;
        setBusy(true);
        try {
            const fetcher = session.consoleMode ? authenticatedFetch : pressingFetch;
            const res = await fetcher('/api/store-pickup/receive', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    presser_id:                  session.presser_id,
                    scan:                        code,
                    console_store_key:           session.console_store_key,
                    console_shopify_location_id: session.console_shopify_location_id,
                    ...(fulfillmentOrderIds && fulfillmentOrderIds.length > 0
                        ? { fulfillment_order_ids: fulfillmentOrderIds }
                        : {}),
                    ...(confirmTransfer ? { confirm_transfer: true } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            // Multi-FO peek: order has 2+ open pickup FOs here — show chooser.
            if (res.ok && data.multi_fo_peek && Array.isArray(data.fulfillments)) {
                setMultiFoPeek({
                    code,
                    order_id:    data.order_id,
                    order_name:  data.order_name || code,
                    fulfillments: data.fulfillments,
                });
                // Pre-check only the not-yet-ready FOs.
                setMultiFoSelected(
                    data.fulfillments
                        .filter(f => !f.already_ready_for_pickup && !f.already_picked_up)
                        .map(f => f.fulfillment_order_id)
                );
                return;
            }
            if (res.ok && data.matched && data.already_picked_up) {
                playError();
                setAlreadyDone({
                    name: data.order_name || code,
                    kind: 'picked_up',
                    // Full pickup history so the modal renders one signed
                    // receipt per pickup event (see modal rendering below).
                    pickup_history:     Array.isArray(data.pickup_history) ? data.pickup_history : [],
                    customer_name:      data.customer_name,
                    associate_name:     data.associate_name,
                    signature_data_url: data.signature_data_url,
                    picked_up_at:       data.picked_up_at,
                });
                setFeed(f => [{ ts: Date.now(), name: data.order_name || code, status: 'mismatch', message: `Already picked up${data.customer_name ? ` by ${data.customer_name}` : ''}` }, ...f].slice(0, 12));
            } else if (res.ok && data.matched && data.already_ready_for_pickup) {
                playError();
                setAlreadyDone({ name: data.order_name || code, kind: 'ready' });
                setFeed(f => [{ ts: Date.now(), name: data.order_name || code, status: 'mismatch', message: 'Already marked ready for pickup' }, ...f].slice(0, 12));
            } else if (res.ok && data.matched && data.already_fulfilled) {
                playError();
                setAlreadyDone({ name: data.order_name || code, kind: 'fulfilled' });
                setFeed(f => [{ ts: Date.now(), name: data.order_name || code, status: 'mismatch', message: 'Order already fulfilled' }, ...f].slice(0, 12));
            } else if (res.ok && data.matched && data.not_transferred) {
                playError();
                {
                    const miss = [];
                    if (!data.picked)  miss.push('picked');
                    if (!data.printed) miss.push('printed');
                    miss.push('transferred');
                    const missingText = miss.length === 1
                        ? miss[0]
                        : miss.slice(0, -1).join(', ') + ' or ' + miss[miss.length - 1];
                    setNotTransferred({
                        name: data.order_name || code,
                        code,
                        qc_passed: !!data.qc_passed,
                        wrong_destination: !!data.wrong_destination,
                        original_store: data.original_store || '',
                        from_location: data.from_location || 'the fulfillment location',
                        store_name: data.store_name || 'this store',
                        missingText,
                        bulk_shipment_labels: Array.isArray(data.bulk_shipment_labels) ? data.bulk_shipment_labels : [],
                    });
                }
                setFeed(f => [{ ts: Date.now(), name: data.order_name || code, status: 'mismatch',
                    message: data.qc_passed
                        ? 'Never transferred — QC passed, tap to transfer + mark ready'
                        : "Never transferred — NOT fully QC'd" }, ...f].slice(0, 12));
            } else if (res.ok && data.matched) {
                const results   = Array.isArray(data.results) ? data.results : [];
                const okCount   = results.filter(r => r.status === 'ok').length;
                const readyCount = results.filter(r => r.status === 'already_ready').length;
                const failCount = results.filter(r => r.status === 'error' || r.status === 'not_ready').length;
                // 2026-09-25 (Eric): Shopify refused the ready mark but the scan
                // is RECORDED as received — this used to be a 409 dead end. The
                // tone is a warning, not a success: no email went out and the
                // modal below tells the operator to contact the customer.
                const refusedOnly = results.length > 0 && okCount === 0 && failCount > 0;
                if (refusedOnly) playError(); else playSuccess();
                let msg = refusedOnly ? 'Received here — Shopify could not mark it ready' : 'Marked ready for pickup';
                if (results.length > 1) {
                    const parts = [];
                    if (okCount)    parts.push(`${okCount} marked ready`);
                    if (readyCount) parts.push(`${readyCount} already ready`);
                    if (failCount)  parts.push(`${failCount} received but not marked ready`);
                    msg = parts.join(', ') || msg;
                }
                if (data.customer_contact_required) {
                    msg += ' — CONTACT CUSTOMER (no Shopify email)';
                    setContactNeeded({ name: data.order_name || code, ...data.customer_contact_required });
                }
                setFeed(f => [{ ts: Date.now(), name: data.order_name || code, status: (data.customer_contact_required || refusedOnly) ? 'mismatch' : 'ok', message: msg,
                    bulk_shipment_labels: Array.isArray(data.bulk_shipment_labels) ? data.bulk_shipment_labels : [] }, ...f].slice(0, 12));
            } else if (res.ok && data.matched === false && data.edited_no_pickup) {
                // TP-edited child order (:A / :A2 / :B ...) whose delivery
                // method got flipped from PICK_UP on the parent to SHIPPING
                // on the child during the Team Portal edit flow. Pre-2026-08-18
                // this surfaced as a nonsense "Wrong Store — belongs at your
                // own store" modal. Show the accurate explanation instead.
                playError();
                setEditedNoPickup({ name: data.order_name || code });
                setFeed(f => [{ ts: Date.now(), name: data.order_name || code, status: 'mismatch', message: 'Edited order — not a pickup order (known bug)' }, ...f].slice(0, 12));
            } else if (res.ok && data.matched === false) {
                playError();
                setMismatch({ name: data.order_name || code, correct_store: data.correct_store || 'Unknown store' });
                setFeed(f => [{ ts: Date.now(), name: data.order_name || code, status: 'mismatch', message: `Belongs at ${data.correct_store || 'unknown'}` }, ...f].slice(0, 12));
            } else {
                playError();
                setFeed(f => [{ ts: Date.now(), name: code, status: 'err', message: data.detail || 'Order not found' }, ...f].slice(0, 12));
            }
        } catch (e) {
            playError();
            setFeed(f => [{ ts: Date.now(), name: code, status: 'err', message: 'Network error' }, ...f].slice(0, 12));
        } finally {
            setBusy(false);
            focusScan();
        }
    };

    return (
        <div className="spp-screen">
            <div className="spp-screen-head">
                <button className="spp-back" onClick={onBack}>← Back</button>
                <div className="spp-screen-title">Receive Orders</div>
            </div>
            <div className="spp-scan-row">
                <input
                    ref={inputRef}
                    className="spp-input spp-input--scan"
                    value={scan}
                    onChange={e => setScan(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(scan); } }}
                    onBlur={() => focusScan()}
                    placeholder="Scan order barcode"
                    autoFocus
                    autoComplete="off"
                    disabled={busy}
                />
                <button
                    type="button"
                    className="spp-camera-btn"
                    onClick={() => setCameraOpen(true)}
                    title="Open camera scanner"
                    disabled={busy}
                >📷</button>
            </div>
            {cameraOpen && (
                <CameraScanner
                    onDetect={(barcode) => {
                        setCameraOpen(false);
                        submit(barcode);
                    }}
                    onClose={() => { setCameraOpen(false); focusScan(); }}
                />
            )}
            <div className="spp-feed">
                {feed.length === 0 && <div className="spp-feed-empty">Scan an order to mark it ready for pickup.</div>}
                {feed.map(f => (
                    <div key={f.ts} className={`spp-feed-item spp-feed-item--${f.status}`}>
                        <div className="spp-feed-item__name">{f.name}</div>
                        {(f.bulk_shipment_labels || []).length > 0 && (
                            <div className="spp-bulk">🚚 Box: {f.bulk_shipment_labels.map(l => (
                                (l.tracking_url ? <a key={l.id} href={l.tracking_url} target="_blank" rel="noreferrer" style={{ marginRight: 10 }}>{l.tracking_number}</a> : <span key={l.id} style={{ marginRight: 10 }}>{l.tracking_number}</span>)
                            ))}</div>
                        )}
                        <div className="spp-feed-item__msg">{f.message}</div>
                    </div>
                ))}
            </div>
            {mismatch && (
                <div className="spp-modal-bg" onClick={() => { setMismatch(null); focusScan(); }}>
                    <div className="spp-modal" onClick={e => e.stopPropagation()}>
                        <div className="spp-modal__icon">⚠️</div>
                        <div className="spp-modal__title">Wrong Store</div>
                        <div className="spp-modal__order">{mismatch.name}</div>
                        <div className="spp-modal__msg">
                            This order is for <strong>{mismatch.correct_store}</strong>.
                            Set it aside and notify the correct store.
                        </div>
                        <button className="spp-modal__btn" onClick={() => { setMismatch(null); focusScan(); }}>
                            Got it
                        </button>
                    </div>
                </div>
            )}
            {notTransferred && (
                <div className="spp-modal-bg spp-modal-bg--alert" onClick={() => { setNotTransferred(null); focusScan(); }}>
                    <div className="spp-modal spp-modal--alert" onClick={e => e.stopPropagation()}>
                        <div className="spp-modal__icon">🚨</div>
                        <div className="spp-modal__title">Not Transferred</div>
                        <div className="spp-modal__order">{notTransferred.name}</div>
                        {(notTransferred.bulk_shipment_labels || []).length > 0 && (
                            <div className="spp-bulk">🚚 Box: {notTransferred.bulk_shipment_labels.map(l => (
                                (l.tracking_url ? <a key={l.id} href={l.tracking_url} target="_blank" rel="noreferrer" style={{ marginRight: 10 }}>{l.tracking_number}</a> : <span key={l.id} style={{ marginRight: 10 }}>{l.tracking_number}</span>)
                            ))}</div>
                        )}
                        {notTransferred.wrong_destination ? (
                            <>
                                <div className="spp-modal__msg">
                                    Order was never transferred from <b>{notTransferred.from_location}</b> and
                                    is set for pickup at <b>{notTransferred.original_store}</b> —
                                    not {notTransferred.store_name}. Double-check the label;
                                    do not receive it here.
                                </div>
                                <button className="spp-modal__btn" onClick={() => { setNotTransferred(null); focusScan(); }}>
                                    Got it
                                </button>
                            </>
                        ) : notTransferred.qc_passed ? (
                            <>
                                <div className="spp-modal__msg">
                                    Order was never transferred from <b>{notTransferred.from_location}</b>,
                                    but was successfully QC'd. Please double-check the location on
                                    the label — the button below will transfer this order
                                    to <b>{notTransferred.store_name}</b> and mark it ready for pickup.
                                </div>
                                <button className="spp-modal__btn spp-modal__btn--danger"
                                        onClick={() => {
                                            const c = notTransferred.code;
                                            setNotTransferred(null);
                                            submit(c, null, true);
                                        }}>
                                    Transfer to {notTransferred.store_name} &amp; Mark Ready
                                </button>
                                <button className="spp-modal__btn"
                                        onClick={() => { setNotTransferred(null); focusScan(); }}>
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="spp-modal__msg">
                                    This order has <b>not</b> been {notTransferred.missingText} from{' '}
                                    <b>{notTransferred.from_location}</b> and has <b>NOT</b> fully
                                    passed QC. Set it aside and notify management — the warehouse
                                    must finish and QC it before it can be transferred
                                    to {notTransferred.store_name}.
                                </div>
                                <button className="spp-modal__btn" onClick={() => { setNotTransferred(null); focusScan(); }}>
                                    Got it
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
            {editedNoPickup && (
                <div className="spp-modal-bg" onClick={() => { setEditedNoPickup(null); focusScan(); }}>
                    <div className="spp-modal" onClick={e => e.stopPropagation()}>
                        <div className="spp-modal__icon">✏️</div>
                        <div className="spp-modal__title">Edited Order</div>
                        <div className="spp-modal__order">{editedNoPickup.name}</div>
                        <div className="spp-modal__msg">
                            This order is edited, and not a pickup order — if you think that it is,
                            you will need to contact the customer directly that the order is ready
                            for pickup. This is a known bug that's being fixed.
                        </div>
                        <button className="spp-modal__btn" onClick={() => { setEditedNoPickup(null); focusScan(); }}>
                            Got it
                        </button>
                    </div>
                </div>
            )}
            {multiFoPeek && (
                <div className="spp-modal-bg">
                    <div className="spp-modal spp-multi-modal" onClick={e => e.stopPropagation()}>
                        <div className="spp-modal__icon">📦</div>
                        <div className="spp-modal__title">Which fulfillments to mark ready?</div>
                        <div className="spp-modal__order">{multiFoPeek.order_name}</div>
                        <div className="spp-modal__msg">
                            This order has <strong>{multiFoPeek.fulfillments.length} fulfillments</strong> at this store.
                            Check each one you're marking as ready for pickup.
                        </div>
                        <div className="spp-multi-fo-list">
                            {multiFoPeek.fulfillments.map(f => {
                                const foId = f.fulfillment_order_id;
                                const done = f.already_ready_for_pickup || f.already_picked_up;
                                const checked = multiFoSelected.includes(foId);
                                const players = Array.isArray(f.players) ? f.players : [];
                                const items   = Array.isArray(f.items) ? f.items : [];
                                const playerLabel = players.length
                                    ? players.map(p => p.number ? `${p.name} #${p.number}` : p.name).join(', ')
                                    : '';
                                return (
                                    <div key={foId}
                                         className={`spp-fo-card ${checked && !done ? 'spp-fo-card--on' : ''} ${done ? 'spp-fo-card--done' : ''}`}
                                         onClick={() => {
                                             if (done) return;
                                             setMultiFoSelected(sel =>
                                                 sel.includes(foId)
                                                     ? sel.filter(x => x !== foId)
                                                     : [...sel, foId]);
                                         }}>
                                        <div className="spp-fo-card__head">
                                            <input type="checkbox" checked={checked && !done} disabled={done}
                                                   onChange={() => {
                                                       if (done) return;
                                                       setMultiFoSelected(sel =>
                                                           sel.includes(foId)
                                                               ? sel.filter(x => x !== foId)
                                                               : [...sel, foId]);
                                                   }}
                                                   onClick={e => e.stopPropagation()} />
                                            <div className="spp-fo-card__title">
                                                <div className="spp-fo-card__player">
                                                    {playerLabel || `Fulfillment ${(foId || '').split('/').pop()}`}
                                                </div>
                                                <div className="spp-fo-card__meta">
                                                    {(f.items_count || 0)} item{f.items_count === 1 ? '' : 's'}
                                                    {f.already_ready_for_pickup ? ' • Already ready' : ''}
                                                    {f.already_picked_up ? ' • Already picked up' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        {(() => {
                                            // One preview per FO — the customer's assembled design.
                                            // Attributes are aggregated across all line items in
                                            // the FO backend-side so `getPreviewImageUrls` finds
                                            // `Preview front` / `Preview back` regardless of
                                            // which sibling line item carries them.
                                            const { front, back } = getPreviewImageUrls(f.preview_attrs || []);
                                            const src     = shopifyResize(front || f.fallback_image_url, 200);
                                            const srcBack = back ? shopifyResize(back, 200) : null;
                                            const hasImage = !!src;
                                            return (
                                                <div className="spp-fo-card__body">
                                                    {hasImage && (
                                                        <div className="spp-fo-card__previews">
                                                            <img src={src} alt="Front" className="spp-fo-card__preview" loading="lazy" />
                                                            {srcBack && (
                                                                <img src={srcBack} alt="Back" className="spp-fo-card__preview" loading="lazy" />
                                                            )}
                                                        </div>
                                                    )}
                                                    {items.length > 0 && (
                                                        <ul className="spp-fo-card__items">
                                                            {items.map(it => (
                                                                <li key={it.line_item_id} className="spp-fo-card__item">
                                                                    <span className="spp-fo-card__item-qty">{it.quantity}×</span>
                                                                    <span className="spp-fo-card__item-title">{it.title || it.sku}</span>
                                                                    {it.sku && it.sku !== it.title && (
                                                                        <span className="spp-fo-card__item-sku">{it.sku}</span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="spp-multi-modal__actions">
                            <button className="spp-modal__btn spp-modal__btn--secondary"
                                    onClick={() => { setMultiFoPeek(null); setMultiFoSelected([]); focusScan(); }}
                                    disabled={busy}>
                                Cancel
                            </button>
                            <button className="spp-modal__btn spp-modal__btn--primary"
                                    disabled={busy || multiFoSelected.length === 0}
                                    onClick={() => {
                                        const chosen = multiFoSelected.slice();
                                        const code = multiFoPeek.code;
                                        setMultiFoPeek(null);
                                        setMultiFoSelected([]);
                                        submit(code, chosen);
                                    }}>
                                Mark {multiFoSelected.length} ready
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {contactNeeded && (
                <div className="spp-modal-bg" onClick={() => { setContactNeeded(null); focusScan(); }}>
                    <div className="spp-modal" onClick={e => e.stopPropagation()}>
                        <div className="spp-modal__icon">📞</div>
                        <div className="spp-modal__title">Contact the customer directly</div>
                        <div className="spp-modal__order">{contactNeeded.name}</div>
                        <div className="spp-modal__msg">
                            {contactNeeded.reason === 'shopify_refused' ? (
                                <>This order is <b>recorded as received here</b>, but Shopify would <b>not</b> mark it
                                ready for pickup, so <b>no email went to the customer</b>.
                                {contactNeeded.detail ? <><br /><span style={{ fontWeight: 400 }}>Shopify: {contactNeeded.detail}</span><br /></> : ' '}
                                <b>Scan it again in a minute</b> so Shopify can email the customer. If it still fails,
                                please <b>call or email the customer</b> that their order is ready for pickup.</>
                            ) : contactNeeded.reason === 'override_shipping' ? (
                                <>This ship-to order has a pickup override to <b>{contactNeeded.override_store}</b>, so
                                Shopify will <b>not</b> email the customer.
                                {' '}It is marked ready in OpsHub — please <b>call or email the customer</b> that their order is ready for pickup.</>
                            ) : (
                                <>This order's pickup location was overridden
                                {contactNeeded.original_store ? <> from <b>{contactNeeded.original_store}</b></> : null}
                                {' '}to <b>{contactNeeded.override_store}</b>, so Shopify will <b>not</b> email the customer.
                                {' '}It is marked ready in OpsHub — please <b>call or email the customer</b> that their order is ready for pickup.</>
                            )}
                        </div>
                        <div className="spp-modal__msg" style={{ marginTop: 10, fontWeight: 600 }}>
                            {contactNeeded.customer_name || 'Customer'}
                            {contactNeeded.customer_email ? <><br />{contactNeeded.customer_email}</> : null}
                            {contactNeeded.customer_phone ? <><br />{contactNeeded.customer_phone}</> : null}
                            {!contactNeeded.customer_email && !contactNeeded.customer_phone ? <><br /><span style={{ fontWeight: 400 }}>No contact details on the order — check Shopify.</span></> : null}
                        </div>
                        <button className="spp-modal__btn spp-modal__btn--danger"
                                onClick={() => { setContactNeeded(null); focusScan(); }}>
                            I'll contact the customer
                        </button>
                    </div>
                </div>
            )}
            {alreadyDone && (
                <div className="spp-modal-bg" onClick={() => { setAlreadyDone(null); focusScan(); }}>
                    <div className="spp-modal" onClick={e => e.stopPropagation()}>
                        <div className="spp-modal__icon">{alreadyDone.kind === 'picked_up' ? '✅' : 'ℹ️'}</div>
                        <div className="spp-modal__title">
                            {alreadyDone.kind === 'picked_up' ? 'Already Picked Up'
                              : alreadyDone.kind === 'ready'  ? 'Already Marked Ready'
                              : 'Order Already Fulfilled'}
                        </div>
                        <div className="spp-modal__order">{alreadyDone.name}</div>
                        <div className="spp-modal__msg">
                            {alreadyDone.kind === 'picked_up'
                                ? 'This order was already picked up by the customer.'
                              : alreadyDone.kind === 'ready'
                                ? 'This order has already been marked ready for pickup. The customer has been notified.'
                                : 'This order has already been fulfilled. No further action needed.'}
                        </div>
                        {alreadyDone.kind === 'picked_up' && (() => {
                            // Prefer full pickup_history; fall back to legacy single-pickup fields.
                            const history = Array.isArray(alreadyDone.pickup_history) && alreadyDone.pickup_history.length > 0
                                ? alreadyDone.pickup_history
                                : (alreadyDone.customer_name || alreadyDone.signature_data_url
                                    ? [{
                                        customer_name:      alreadyDone.customer_name,
                                        associate_name:     alreadyDone.associate_name,
                                        signature_data_url: alreadyDone.signature_data_url,
                                        picked_up_at:       alreadyDone.picked_up_at,
                                    }]
                                    : []);
                            if (history.length === 0) return null;
                            const multi = history.length > 1;
                            return (
                                <div style={{ margin: '4px 0 14px', textAlign: 'left', maxHeight: 340, overflowY: 'auto' }}>
                                    {history.map((h, idx) => (
                                        <div key={idx} style={{
                                            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
                                            padding: 10, marginBottom: idx < history.length - 1 ? 8 : 0,
                                        }}>
                                            {multi && (
                                                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                                                    Pickup {idx + 1} of {history.length}
                                                    {Array.isArray(h.fulfillment_order_ids) && h.fulfillment_order_ids.length > 0 && (
                                                        <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                                                            · {h.fulfillment_order_ids.length} fulfillment{h.fulfillment_order_ids.length === 1 ? '' : 's'}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {h.customer_name && (
                                                <div style={{ fontSize: 13, color: '#0d2240', marginBottom: 4 }}>
                                                    <strong>Picked up by:</strong> {h.customer_name}
                                                </div>
                                            )}
                                            {h.associate_name && (
                                                <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 4 }}>
                                                    Released by {h.associate_name}
                                                </div>
                                            )}
                                            {h.picked_up_at && (
                                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: h.signature_data_url ? 6 : 0 }}>
                                                    {new Date(h.picked_up_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                                </div>
                                            )}
                                            {h.signature_data_url && (
                                                <div>
                                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Signature</div>
                                                    <img src={h.signature_data_url} alt="Customer signature"
                                                         style={{ maxWidth: '100%', maxHeight: 80, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4 }} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                        <button className="spp-modal__btn" onClick={() => { setAlreadyDone(null); focusScan(); }}>
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function OrderPickupScreen({ session, onBack }) {
    const [step, setStep] = useState('scan');  // 'scan' | 'choose-fos' | 'sign' | 'done'
    const [scan, setScan] = useState('');
    const [order, setOrder] = useState(null);  // {order_id, name, customer_name, items_count, fulfillments?}
    const [customerName, setCustomerName] = useState('');
    const [signing, setSigning] = useState(false);
    const [error, setError] = useState('');
    const [alreadyPickedUp, setAlreadyPickedUp] = useState(null);  // {name, customer_name, signature_data_url, associate_name}
    const [cameraOpen, setCameraOpen] = useState(false);
    const [searchResults, setSearchResults] = useState([]);   // [{order_id, order_name, customer_name, items_count, store_key}]
    const [searching, setSearching] = useState(false);
    // Multi-FO chooser: GIDs the associate has selected for this pickup.
    // Defaults to ALL FOs when the chooser opens (most customers take
    // everything; deselect what they're leaving).
    const [selectedFoIds, setSelectedFoIds] = useState([]);
    const inputRef = useRef(null);
    const sigRef = useRef(null);
    const searchAbortRef = useRef(null);

    useEffect(() => {
        if (step === 'scan') { setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 50); }
    }, [step]);

    // Debounced typeahead: as the user types (3+ chars), search RFP orders at
    // their assigned pickup store. A long string with no spaces is assumed to
    // be a barcode and skipped — Enter handles those via lookup().
    useEffect(() => {
        if (step !== 'scan') return;
        const q = scan.trim();
        if (q.length < 3) { setSearchResults([]); return; }
        // Pure-numeric or order-name barcodes are handled by lookup(); don't
        // hit the typeahead endpoint for them. Any whitespace or alpha-only
        // query is treated as a name search.
        if (/^[0-9-]+$/.test(q) && q.length < 4) { setSearchResults([]); return; }
        const handle = setTimeout(async () => {
            try {
                if (searchAbortRef.current) searchAbortRef.current.abort();
                const ac = new AbortController();
                searchAbortRef.current = ac;
                setSearching(true);
                const fetcher = session.consoleMode ? authenticatedFetch : pressingFetch;
                const res = await fetcher('/api/store-pickup/search', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        presser_id:                  session.presser_id,
                        q,
                        console_store_key:           session.console_store_key,
                        console_shopify_location_id: session.console_shopify_location_id,
                    }),
                    signal: ac.signal,
                });
                const data = await res.json().catch(() => ({}));
                setSearchResults(Array.isArray(data.orders) ? data.orders : []);
            } catch (e) {
                if (e.name !== 'AbortError') setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [scan, step, session]);

    const lookup = async (raw) => {
        const code = (raw ?? scan).trim();
        if (!code) return;
        setError('');
        try {
            const fetcher = session.consoleMode ? authenticatedFetch : pressingFetch;
            const res = await fetcher('/api/store-pickup/lookup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    presser_id:                  session.presser_id,
                    scan:                        code,
                    console_store_key:           session.console_store_key,
                    console_shopify_location_id: session.console_shopify_location_id,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                playError();
                setError(data.detail || 'Order not found');
                return;
            }
            if (!data.matched) {
                playError();
                // Build a richer mismatch / not-pickup-able message: where the
                // order currently sits, where the customer wanted to pick up,
                // and the current fulfillment status. Helps the associate
                // explain what's going on instead of just "wrong store".
                const dest = data.pickup_destination_name || data.correct_store || 'a different store';
                const here = data.current_location_name || '';
                const status = (data.display_fulfillment_status || '').replace(/_/g, ' ').toLowerCase();
                let msg = `${data.order_name || 'This order'} → pickup at ${dest}`;
                if (here && here !== dest) msg += `\nCurrently at: ${here}`;
                if (status) msg += `\nStatus: ${status}`;
                setError(msg);
                return;
            }
            if (data.already_picked_up) {
                playError();
                setAlreadyPickedUp({
                    name:               data.order_name || data.name,
                    // Full pickup history (each entry: customer_name, associate_name,
                    // signature_data_url, picked_up_at, fulfillment_order_ids). When
                    // this is populated the modal renders one signed receipt per
                    // pickup event; the legacy single-pickup fields below stay for
                    // backward compat with older backend responses.
                    pickup_history:     Array.isArray(data.pickup_history) ? data.pickup_history : [],
                    customer_name:      data.customer_name,
                    associate_name:     data.associate_name,
                    signature_data_url: data.signature_data_url,
                });
                setScan('');
                return;
            }
            // 2026-08-09: refuse to advance to signature capture if no FO on
            // this order has been marked ready for pickup at this location.
            // Prior behavior trusted the destination-match rule and jumped
            // straight to signature for orders still in-transit — SP-339296
            // canonical.
            if (data.any_ready_here === false) {
                playError();
                const here = data.current_location_name || '';
                const dest = data.pickup_destination_name || '';
                let msg = `${data.order_name || 'This order'} is not ready for pickup yet.`;
                if (here && here !== dest) msg += `\nCurrently at: ${here}`;
                if (dest) msg += `\nPickup destination: ${dest}`;
                msg += `\n\nUse Receive Orders to mark it ready when it arrives at the store.`;
                setError(msg);
                setScan('');
                return;
            }
            playSuccess();
            setOrder(data);
            // Customer printed name must be typed in person — never pre-fill.
            setCustomerName('');
            setScan('');
            // Multi-FO: when the order has 2+ open pickup FOs at this location
            // (typically from a partial transfer + a remainder), let the
            // associate pick which one(s) the customer is taking. Default to
            // ALL selected so the common "they're taking everything" path is
            // one tap (Continue) — deselect anything being left for later.
            const fulfillments = Array.isArray(data.fulfillments) ? data.fulfillments : [];
            if (fulfillments.length > 1) {
                setSelectedFoIds(fulfillments.map(f => f.fulfillment_order_id).filter(Boolean));
                setStep('choose-fos');
            } else {
                setSelectedFoIds(fulfillments[0]?.fulfillment_order_id
                    ? [fulfillments[0].fulfillment_order_id]
                    : []);
                setStep('sign');
            }
        } catch (e) {
            playError();
            setError('Network error');
        }
    };

    const submitPickup = async () => {
        if (!sigRef.current?.hasInk?.()) { setError('Customer signature is required.'); return; }
        if (!customerName.trim()) { setError('Customer name is required.'); return; }
        setSigning(true);
        setError('');
        try {
            const png = sigRef.current.toDataURL();
            const fetcher = session.consoleMode ? authenticatedFetch : pressingFetch;
            const res = await fetcher('/api/store-pickup/pickup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    presser_id:                  session.presser_id,
                    order_id:                    order.order_id,
                    store_key:                   order.store_key,
                    customer_name:               customerName.trim(),
                    signature_png:               png,
                    console_store_key:           session.console_store_key,
                    console_shopify_location_id: session.console_shopify_location_id,
                    // Multi-FO: explicit list of FO GIDs this signature covers.
                    // Backend validates each is still an open PICK_UP FO at
                    // the user's location. Omit when the user came from the
                    // single-FO path so the backend keeps its existing behavior.
                    fulfillment_order_ids:       selectedFoIds.length > 0 ? selectedFoIds : undefined,
                }),
            });
            if (!res.ok) {
                playError();
                const d = await res.json().catch(() => ({}));
                setError(d.detail || 'Pickup failed.');
                setSigning(false);
                return;
            }
            playSuccess();
            setStep('done');
        } catch (e) {
            playError();
            setError('Network error');
        } finally {
            setSigning(false);
        }
    };

    const reset = () => {
        setOrder(null);
        setCustomerName('');
        setScan('');
        setError('');
        setSelectedFoIds([]);
        setStep('scan');
    };

    const toggleFo = (foId) => {
        setSelectedFoIds(prev => prev.includes(foId)
            ? prev.filter(id => id !== foId)
            : [...prev, foId]);
    };

    if (step === 'scan') {
        return (
            <div className="spp-screen">
                <div className="spp-screen-head">
                    <button className="spp-back" onClick={onBack}>← Back</button>
                    <div className="spp-screen-title">Order Pickup</div>
                </div>
                <div className="spp-scan-row">
                <input
                    ref={inputRef}
                    className="spp-input spp-input--scan"
                    value={scan}
                    onChange={e => setScan(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookup(); } }}
                    onBlur={() => setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 50)}
                    placeholder="Scan or search by customer / order #"
                    autoFocus
                    autoComplete="off"
                />
                <button
                    type="button"
                    className="spp-camera-btn"
                    onClick={() => setCameraOpen(true)}
                    title="Open camera scanner"
                >📷</button>
                </div>
                {cameraOpen && (
                    <CameraScanner
                        onDetect={(barcode) => {
                            setCameraOpen(false);
                            lookup(barcode);
                        }}
                        onClose={() => { setCameraOpen(false); setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 50); }}
                    />
                )}
                {searchResults.length > 0 && (
                    <div className="spp-search-results">
                        {searchResults.map(r => {
                            const ss = r.store_status ?? 0;
                            const statusLabel = ss === 3 ? 'Picked Up'
                                : ss === 2 ? 'Ready'
                                : ss === 1 ? 'In Transit'
                                : (r.fulfillment_status || '').replace(/_/g, ' ').toLowerCase();
                            const players = Array.isArray(r.players) ? r.players : [];
                            return (
                                <button
                                    type="button"
                                    key={`${r.store_key}-${r.order_id}`}
                                    className="spp-search-row"
                                    onClick={() => {
                                        setSearchResults([]);
                                        lookup(r.order_name || String(r.order_id));
                                    }}
                                >
                                    <span className="spp-search-row__name">{r.order_name}</span>
                                    <span className="spp-search-row__customer">
                                        {r.customer_name || '—'}
                                        {players.length > 0 && (
                                            <span className="spp-search-row__players"> · {players.join(', ')}</span>
                                        )}
                                    </span>
                                    <span className={`spp-search-row__status spp-search-row__status--${ss}`}>{statusLabel || '—'}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {searchResults.length === 0 && scan.trim().length >= 3 && !searching && (
                    <div className="spp-search-empty">No ready-for-pickup orders match.</div>
                )}
                {error && <div className="spp-error">{error}</div>}
                {alreadyPickedUp && (
                    <div className="spp-modal-bg" onClick={() => { setAlreadyPickedUp(null); setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 50); }}>
                        <div className="spp-modal" onClick={e => e.stopPropagation()}>
                            <div className="spp-modal__icon">✅</div>
                            <div className="spp-modal__title">Already Picked Up</div>
                            <div className="spp-modal__order">{alreadyPickedUp.name}</div>
                            <div className="spp-modal__msg">
                                This order was already picked up by the customer.
                            </div>
                            {(() => {
                                // Prefer full pickup_history (one card per pickup event).
                                // Fall back to the single-pickup legacy fields when the
                                // backend didn't populate history (older responses, or
                                // orders picked up entirely outside Ops Hub).
                                const history = Array.isArray(alreadyPickedUp.pickup_history) && alreadyPickedUp.pickup_history.length > 0
                                    ? alreadyPickedUp.pickup_history
                                    : (alreadyPickedUp.customer_name || alreadyPickedUp.signature_data_url
                                        ? [{
                                            customer_name:      alreadyPickedUp.customer_name,
                                            associate_name:     alreadyPickedUp.associate_name,
                                            signature_data_url: alreadyPickedUp.signature_data_url,
                                        }]
                                        : []);
                                if (history.length === 0) return null;
                                const multi = history.length > 1;
                                return (
                                    <div style={{ margin: '4px 0 14px', textAlign: 'left', maxHeight: 340, overflowY: 'auto' }}>
                                        {history.map((h, idx) => (
                                            <div key={idx} style={{
                                                background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
                                                padding: 10, marginBottom: idx < history.length - 1 ? 8 : 0,
                                            }}>
                                                {multi && (
                                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                                                        Pickup {idx + 1} of {history.length}
                                                        {Array.isArray(h.fulfillment_order_ids) && h.fulfillment_order_ids.length > 0 && (
                                                            <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                                                                · {h.fulfillment_order_ids.length} fulfillment{h.fulfillment_order_ids.length === 1 ? '' : 's'}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {h.customer_name && (
                                                    <div style={{ fontSize: 13, color: '#0d2240', marginBottom: 4 }}>
                                                        <strong>Picked up by:</strong> {h.customer_name}
                                                    </div>
                                                )}
                                                {h.associate_name && (
                                                    <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 4 }}>
                                                        Released by {h.associate_name}
                                                    </div>
                                                )}
                                                {h.picked_up_at && (
                                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: h.signature_data_url ? 6 : 0 }}>
                                                        {new Date(h.picked_up_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                                    </div>
                                                )}
                                                {h.signature_data_url && (
                                                    <div>
                                                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Signature</div>
                                                        <img src={h.signature_data_url} alt="Customer signature"
                                                             style={{ maxWidth: '100%', maxHeight: 80, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4 }} />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                            <button className="spp-modal__btn" onClick={() => { setAlreadyPickedUp(null); setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 50); }}>
                                Got it
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (step === 'choose-fos') {
        const fulfillments = Array.isArray(order?.fulfillments) ? order.fulfillments : [];
        const renderPlayers = (players) => {
            const list = Array.isArray(players) ? players : [];
            if (list.length === 0) return null;
            return list.map(p => {
                const name = (p?.name || '').trim();
                const num  = (p?.number || '').trim();
                if (name && num) return `${name} #${num}`;
                if (name) return name;
                if (num) return `#${num}`;
                return '';
            }).filter(Boolean).join(', ');
        };
        return (
            <div className="spp-screen">
                <div className="spp-screen-head">
                    <button className="spp-back" onClick={reset}>← Cancel</button>
                    <div className="spp-screen-title">{order.name}</div>
                </div>
                <div className="spp-pickup-meta">
                    <div className="spp-pickup-meta__row"><strong>Customer:</strong> {order.customer_name || '—'}</div>
                    <div className="spp-pickup-meta__row" style={{ color: '#fbbf24' }}>
                        <strong>This order has {fulfillments.length} fulfillments at this store.</strong>
                    </div>
                    <div className="spp-pickup-meta__row" style={{ fontSize: 13 }}>
                        Select what the customer is picking up now. All are selected by default.
                    </div>
                    {(order?.bulk_shipment_labels || []).length > 0 && (
                        <div className="spp-pickup-meta__row spp-pickup-meta__row--bulk">🚚 Box: {order.bulk_shipment_labels.map(l => (
                            (l.tracking_url ? <a key={l.id} href={l.tracking_url} target="_blank" rel="noreferrer" style={{ marginRight: 10 }}>{l.tracking_number}</a> : <span key={l.id} style={{ marginRight: 10 }}>{l.tracking_number}</span>)
                        ))}</div>
                    )}
                </div>
                {order.prior_pickup && (
                    <div className="spp-prior-pickup" style={{
                        background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6,
                        padding: '10px 12px', margin: '8px 0 12px', color: '#78350f', fontSize: 13,
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            ⚠ Partial pickup — customer previously picked up part of this order.
                        </div>
                        <div>
                            {order.prior_pickup.customer_name ? `Picked up by ${order.prior_pickup.customer_name}` : 'Prior pickup'}
                            {order.prior_pickup.picked_up_at && (
                                <> on {new Date(order.prior_pickup.picked_up_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</>
                            )}
                            {order.prior_pickup.associate_name && <>, released by {order.prior_pickup.associate_name}</>}.
                        </div>
                    </div>
                )}
                <div className="spp-fo-list">
                    {fulfillments.map(f => {
                        const foId = f.fulfillment_order_id;
                        const checked = selectedFoIds.includes(foId);
                        const playersStr = renderPlayers(f.players);
                        const items = Array.isArray(f.items) ? f.items : [];
                        const { front, back } = getPreviewImageUrls(f.preview_attrs || []);
                        const src     = shopifyResize(front || f.fallback_image_url, 200);
                        const srcBack = back ? shopifyResize(back, 200) : null;
                        return (
                            <label key={foId}
                                   className={`spp-fo-card ${checked ? 'spp-fo-card--on' : ''}`}
                                   style={{ display: 'block', cursor: 'pointer' }}>
                                <div className="spp-fo-card__head">
                                    <input type="checkbox"
                                           checked={checked}
                                           onChange={() => toggleFo(foId)} />
                                    <div className="spp-fo-card__title">
                                        <div className="spp-fo-card__player">
                                            {playersStr || `${f.items_count ?? 0} item${f.items_count === 1 ? '' : 's'}`}
                                        </div>
                                        <div className="spp-fo-card__meta">
                                            {(f.items_count ?? 0)} item{f.items_count === 1 ? '' : 's'}
                                        </div>
                                    </div>
                                </div>
                                <div className="spp-fo-card__body">
                                    {src && (
                                        <div className="spp-fo-card__previews">
                                            <img src={src} alt="Front" className="spp-fo-card__preview" loading="lazy" />
                                            {srcBack && (
                                                <img src={srcBack} alt="Back" className="spp-fo-card__preview" loading="lazy" />
                                            )}
                                        </div>
                                    )}
                                    {items.length > 0 && (
                                        <ul className="spp-fo-card__items">
                                            {items.map(it => (
                                                <li key={it.line_item_id} className="spp-fo-card__item">
                                                    <span className="spp-fo-card__item-qty">{it.quantity}×</span>
                                                    <span className="spp-fo-card__item-title">{it.title || it.sku}</span>
                                                    {it.sku && it.sku !== it.title && (
                                                        <span className="spp-fo-card__item-sku">{it.sku}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </label>
                        );
                    })}
                </div>
                {error && <div className="spp-error">{error}</div>}
                <button
                    className="spp-submit"
                    onClick={() => {
                        if (selectedFoIds.length === 0) {
                            setError('Select at least one fulfillment to continue.');
                            return;
                        }
                        setError('');
                        setStep('sign');
                    }}
                    disabled={selectedFoIds.length === 0}
                >
                    Continue ({selectedFoIds.length} of {fulfillments.length})
                </button>
            </div>
        );
    }

    if (step === 'sign') {
        const fulfillments = Array.isArray(order?.fulfillments) ? order.fulfillments : [];
        const isMultiFo = fulfillments.length > 1;
        const selectedFulfillments = isMultiFo
            ? fulfillments.filter(f => selectedFoIds.includes(f.fulfillment_order_id))
            : fulfillments;
        const selectedItemsCount = isMultiFo
            ? selectedFulfillments.reduce((a, f) => a + (f.items_count || 0), 0)
            : (order.items_count ?? null);
        return (
            <div className="spp-screen">
                <div className="spp-screen-head">
                    <button className="spp-back" onClick={() => {
                        // From multi-FO sign step, "back" returns to chooser
                        // so the associate can adjust the selection without
                        // re-scanning. Single-FO orders return to scan.
                        if (isMultiFo) { setStep('choose-fos'); }
                        else { reset(); }
                    }}>← {isMultiFo ? 'Back' : 'Cancel'}</button>
                    <div className="spp-screen-title">{order.name}</div>
                </div>
                <div className="spp-pickup-meta">
                    <div className="spp-pickup-meta__row"><strong>Customer:</strong> {order.customer_name || '—'}</div>
                    <div className="spp-pickup-meta__row"><strong>Club:</strong> {order.club || '—'}</div>
                    {order.player_number && (
                        <div className="spp-pickup-meta__row"><strong>Player Number:</strong> {order.player_number}</div>
                    )}
                    <div className="spp-pickup-meta__row">
                        <strong>{isMultiFo ? 'Items being picked up' : 'Total Items'}:</strong>{' '}
                        {selectedItemsCount ?? '—'}
                        {isMultiFo && (
                            <span style={{ color: '#94a3b8', marginLeft: 8 }}>
                                ({selectedFoIds.length} of {fulfillments.length} fulfillments)
                            </span>
                        )}
                    </div>
                    {(order?.bulk_shipment_labels || []).length > 0 && (
                        <div className="spp-pickup-meta__row spp-pickup-meta__row--bulk">🚚 Box: {order.bulk_shipment_labels.map(l => (
                            (l.tracking_url ? <a key={l.id} href={l.tracking_url} target="_blank" rel="noreferrer" style={{ marginRight: 10 }}>{l.tracking_number}</a> : <span key={l.id} style={{ marginRight: 10 }}>{l.tracking_number}</span>)
                        ))}</div>
                    )}
                </div>
                {order.prior_pickup && (
                    <div className="spp-prior-pickup" style={{
                        background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6,
                        padding: '10px 12px', margin: '8px 0 12px', color: '#78350f', fontSize: 13,
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            ⚠ Partial pickup — customer previously picked up part of this order.
                        </div>
                        <div>
                            {order.prior_pickup.customer_name ? `Picked up by ${order.prior_pickup.customer_name}` : 'Prior pickup'}
                            {order.prior_pickup.picked_up_at && (
                                <> on {new Date(order.prior_pickup.picked_up_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</>
                            )}
                            {order.prior_pickup.associate_name && <>, released by {order.prior_pickup.associate_name}</>}.
                            <br />You are now signing for the <strong>remaining items</strong> listed above.
                        </div>
                    </div>
                )}
                <label className="spp-label">Customer Printed Name <span style={{ color: '#fca5a5' }}>*</span></label>
                <input
                    className="spp-input"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                    placeholder=""
                    autoComplete="off"
                    required
                />
                <label className="spp-label">Customer Signature <span style={{ color: '#fca5a5' }}>*</span></label>
                <SignaturePad ref={sigRef} />
                {error && <div className="spp-error">{error}</div>}
                <button className="spp-submit" onClick={submitPickup} disabled={signing}>
                    {signing ? 'Submitting…' : 'Confirm Pickup'}
                </button>
            </div>
        );
    }

    // done
    return (
        <div className="spp-screen">
            <div className="spp-done">
                <div className="spp-done__icon">✓</div>
                <div className="spp-done__title">Pickup recorded</div>
                <div className="spp-done__sub">{order.name} marked picked up.</div>
                <button className="spp-submit" onClick={reset}>Next Pickup</button>
                <button className="spp-back-big" onClick={onBack}>Back to Menu</button>
            </div>
        </div>
    );
}

// ── Signature pad (HTML canvas, finger-draw friendly) ──────────────────────
const SignaturePad = React.forwardRef(function SignaturePad(_props, ref) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const last = useRef(null);
    const inkedRef = useRef(false);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        // Match canvas drawing buffer to its rendered size for crisp lines.
        const dpr = window.devicePixelRatio || 1;
        const rect = c.getBoundingClientRect();
        c.width = Math.round(rect.width * dpr);
        c.height = Math.round(rect.height * dpr);
        const ctx = c.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0d2240';
    }, []);

    const ptFromEvent = (e) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        const t = e.touches?.[0] || e.changedTouches?.[0];
        const cx = t ? t.clientX : e.clientX;
        const cy = t ? t.clientY : e.clientY;
        return { x: cx - rect.left, y: cy - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        last.current = ptFromEvent(e);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const c = canvasRef.current;
        const ctx = c.getContext('2d');
        const p = ptFromEvent(e);
        if (last.current) {
            ctx.beginPath();
            ctx.moveTo(last.current.x, last.current.y);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
            inkedRef.current = true;
        }
        last.current = p;
    };
    const end = () => { drawing.current = false; last.current = null; };

    const clear = () => {
        const c = canvasRef.current;
        const ctx = c.getContext('2d');
        ctx.clearRect(0, 0, c.width, c.height);
        inkedRef.current = false;
    };

    React.useImperativeHandle(ref, () => ({
        toDataURL: () => {
            // Trim by re-rendering on a white background so PNG is opaque.
            const c = canvasRef.current;
            const off = document.createElement('canvas');
            off.width = c.width; off.height = c.height;
            const ctx = off.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, off.width, off.height);
            ctx.drawImage(c, 0, 0);
            return off.toDataURL('image/png');
        },
        clear,
        hasInk: () => inkedRef.current,
    }));

    return (
        <div className="spp-sig">
            <canvas
                ref={canvasRef}
                className="spp-sig__canvas"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end} onTouchCancel={end}
            />
            <button type="button" className="spp-sig__clear" onClick={clear}>Clear</button>
        </div>
    );
});

// ── Inline styles ─────────────────────────────────────────────────────────
function SppStyles() {
    return (
        <style>{`
            /* Light theme — matches Picking/Pressing/Packing portal aesthetic.
               Page bg = AppShell gray; cards/inputs = white; text = #202223. */
            .spp-page { position: fixed; inset: 0; background: #f1f2f4; color: #202223; display: flex; flex-direction: column; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; overflow-y: auto; -webkit-overflow-scrolling: touch; }
            .spp-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid #e1e3e5; background: #fff; padding-top: calc(14px + env(safe-area-inset-top)); /* iPad/iPhone notch+status bar; env()=0 elsewhere */ }
            .spp-title { font-size: 18px; font-weight: 700; letter-spacing: 0; color: #202223; }
            .spp-user { font-size: 13px; color: #6d7175; }
            .spp-exit { background: transparent; border: none; color: #6d7175; font-size: 22px; cursor: pointer; padding: 4px 10px; }
            /* Matches the Packing/Pressing portal header sign-out button. */
            .spp-signout { background: #fff; border: 1px solid #e4e7eb; color: #52606d; font-size: 14px; font-weight: 600; cursor: pointer; padding: 8px 14px; border-radius: 10px; white-space: nowrap; }
            .spp-signout:active { background: #e9ebee; }
            .spp-menu { padding: 24px 18px; display: flex; flex-direction: column; gap: 16px; align-items: stretch; max-width: 460px; margin: 0 auto; width: 100%; }
            .spp-store-chip { background: #fff; border: 1px solid #e1e3e5; padding: 10px 14px; border-radius: 12px; text-align: center; font-size: 14px; color: #202223; }
            .spp-loc-search { width: 100%; box-sizing: border-box; padding: 12px 14px; border: 1px solid #c9cccf; border-radius: 12px; font-size: 16px; color: #202223; background: #fff; outline: none; }
            .spp-loc-search:focus { border-color: #005bd3; box-shadow: 0 0 0 2px rgba(0,91,211,0.15); }
            .spp-tile { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px 16px; border: 1px solid #e1e3e5; border-radius: 18px; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.06); transition: transform .12s, box-shadow .12s; }
            .spp-tile:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
            /* Pastel washes matching the Pressing/Picking column tints */
            .spp-tile--receive { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
            .spp-tile--pickup  { background: #eff6ff; color: #1e3a8a; border-color: #bfdbfe; }
            .spp-tile__icon  { font-size: 44px; }
            .spp-tile__title { font-size: 22px; font-weight: 700; }
            .spp-tile__sub   { font-size: 13px; opacity: 0.8; }
            .spp-screen { padding: 16px; display: flex; flex-direction: column; gap: 14px; max-width: 460px; margin: 0 auto; width: 100%; flex: 1; }
            .spp-screen-head { display: flex; align-items: center; gap: 12px; }
            .spp-back { background: #fff; border: 1px solid #c9cccf; color: #202223; padding: 6px 12px; border-radius: 8px; font-size: 14px; cursor: pointer; }
            .spp-back-big { background: #fff; border: 1px solid #c9cccf; color: #202223; padding: 12px 16px; border-radius: 10px; font-size: 16px; cursor: pointer; margin-top: 12px; width: 100%; }
            .spp-screen-title { font-size: 18px; font-weight: 600; color: #202223; }
            .spp-input { padding: 14px 14px; border-radius: 12px; border: 2px solid #c9cccf; font-size: 16px; background: #fff; color: #202223; outline: none; width: 100%; box-sizing: border-box; }
            .spp-input--scan { padding: 18px 16px; font-size: 22px; letter-spacing: 1px; }
            .spp-input:focus { border-color: #2563eb; box-shadow: 0 0 0 4px rgba(37,99,235,0.15); }
            .spp-scan-row { display: flex; gap: 8px; align-items: stretch; }
            .spp-scan-row .spp-input { flex: 1; }
            .spp-camera-btn { flex-shrink: 0; width: 56px; font-size: 26px; background: #fff; color: #202223; border: 2px solid #c9cccf; border-radius: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
            .spp-camera-btn:hover { border-color: #2563eb; }
            .spp-camera-btn:disabled { opacity: 0.5; cursor: not-allowed; }
            .spp-feed { display: flex; flex-direction: column; gap: 8px; max-height: 60vh; overflow-y: auto; }
            .spp-feed-empty { color: #6d7175; font-size: 14px; padding: 16px; text-align: center; }
            .spp-feed-item { background: #fff; border: 1px solid #e1e3e5; border-radius: 10px; padding: 10px 12px; border-left: 4px solid #c9cccf; }
            .spp-feed-item--ok { border-left-color: #19a974; }
            .spp-feed-item--mismatch { border-left-color: #f0b41a; }
            .spp-feed-item--err { border-left-color: #d72c0d; }
            .spp-feed-item__name { font-weight: 600; font-size: 15px; color: #202223; }
            .spp-feed-item__msg  { font-size: 13px; color: #6d7175; margin-top: 2px; }
            .spp-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px; }
            .spp-modal-bg--alert { animation: sppAlertFlash 0.8s ease-in-out infinite alternate; }
            @keyframes sppAlertFlash {
                from { background: rgba(150, 10, 10, 0.55); }
                to   { background: rgba(230, 25, 25, 0.85); }
            }
            .spp-modal--alert { border: 4px solid #b91c1c; }
            .spp-modal__btn--danger { background: #b91c1c; color: #fff; border-color: #b91c1c; margin-bottom: 10px; }
            .spp-modal__btn--danger:active { background: #7f1212; }
            .spp-modal { background: #fff; color: #202223; padding: 28px 22px; border-radius: 16px; max-width: 360px; width: 100%; text-align: center; box-shadow: 0 20px 50px rgba(0,0,0,0.2); }
            .spp-modal__icon { font-size: 56px; line-height: 1; margin-bottom: 8px; }
            .spp-modal__title { font-size: 22px; font-weight: 700; line-height: 1.2; margin: 0 0 4px; color: #202223; }
            .spp-modal__order { font-size: 18px; color: #2563eb; font-weight: 600; margin: 6px 0; }
            .spp-modal__msg { font-size: 15px; color: #4b5563; margin: 12px 0 18px; line-height: 1.4; }
            .spp-modal__btn { background: #2563eb; color: #fff; border: none; padding: 14px 22px; border-radius: 12px; font-size: 16px; font-weight: 600; cursor: pointer; width: 100%; }
            .spp-modal__btn:hover { background: #1d4ed8; }
            .spp-modal__btn:disabled { opacity: 0.5; cursor: not-allowed; }
            .spp-modal__btn--primary { background: #19a974; }
            .spp-modal__btn--primary:hover:not(:disabled) { background: #147a54; }
            .spp-modal__btn--secondary { background: #f3f4f6; color: #374151; }
            .spp-modal__btn--secondary:hover:not(:disabled) { background: #e5e7eb; }
            /* Multi-FO picker modal — wider than the generic spp-modal so
               item thumbnails have room. On phone screens it flexes to full
               width via the max-width fallback + inner padding. */
            .spp-multi-modal { max-width: 560px; text-align: left; padding: 22px 16px; }
            .spp-multi-modal .spp-modal__icon,
            .spp-multi-modal .spp-modal__title,
            .spp-multi-modal .spp-modal__order,
            .spp-multi-modal .spp-modal__msg { text-align: center; }
            .spp-multi-modal__actions { display: flex; gap: 8px; margin-top: 6px; }
            .spp-multi-modal__actions .spp-modal__btn--secondary { flex: 1; }
            .spp-multi-modal__actions .spp-modal__btn--primary { flex: 2; }
            .spp-multi-fo-list { display: flex; flex-direction: column; gap: 12px; max-height: 55vh; overflow-y: auto; margin: 6px 0 14px; padding: 2px; -webkit-overflow-scrolling: touch; }
            .spp-fo-card { background: #fff; border: 2px solid #e1e3e5; border-radius: 12px; padding: 12px; cursor: pointer; transition: border-color .12s, background .12s; }
            .spp-fo-card:hover { border-color: #c9cccf; }
            .spp-fo-card--on { background: #ecfdf5; border-color: #19a974; }
            .spp-fo-card--done { background: #f3f4f6; opacity: 0.75; cursor: not-allowed; }
            .spp-fo-card__head { display: flex; align-items: flex-start; gap: 10px; }
            .spp-fo-card__head input[type="checkbox"] { width: 22px; height: 22px; margin-top: 2px; accent-color: #19a974; flex-shrink: 0; cursor: pointer; }
            .spp-fo-card--done .spp-fo-card__head input[type="checkbox"] { cursor: not-allowed; }
            .spp-fo-card__title { flex: 1; min-width: 0; }
            .spp-fo-card__player { font-size: 15px; font-weight: 700; color: #0d2240; line-height: 1.25; }
            .spp-fo-card__meta { font-size: 12px; color: #6b7280; margin-top: 3px; }
            .spp-fo-card__body { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #e5e7eb; }
            .spp-fo-card__previews { display: flex; gap: 8px; justify-content: center; margin-bottom: 10px; }
            .spp-fo-card__preview { width: 140px; height: 140px; border-radius: 10px; object-fit: cover; background: #fafafa; border: 1px solid #e5e7eb; }
            .spp-fo-card__items { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
            .spp-fo-card__item { display: flex; gap: 8px; align-items: baseline; font-size: 13px; color: #202223; line-height: 1.35; }
            .spp-fo-card__item-qty { font-weight: 700; color: #0d2240; flex-shrink: 0; min-width: 24px; }
            .spp-fo-card__item-title { flex: 1; word-break: break-word; }
            .spp-fo-card__item-sku { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 11px; color: #6b7280; flex-shrink: 0; }
            /* iPhone: single-column layout is already the default; just
               tighten padding + shrink previews so more content fits. */
            @media (max-width: 480px) {
                .spp-multi-modal { padding: 18px 12px; max-width: 100%; border-radius: 12px; }
                .spp-multi-modal .spp-modal__title { font-size: 19px; }
                .spp-multi-modal .spp-modal__msg { margin: 8px 0 12px; font-size: 14px; }
                .spp-multi-fo-list { max-height: 60vh; gap: 10px; }
                .spp-fo-card { padding: 10px; }
                .spp-fo-card__preview { width: 110px; height: 110px; }
                .spp-fo-card__item { font-size: 12px; }
            }
            .spp-pickup-meta { background: #fff; border: 1px solid #e1e3e5; padding: 12px 14px; border-radius: 10px; font-size: 14px; color: #4b5563; display: flex; flex-direction: column; gap: 4px; }
            .spp-bulk { font-size: 13px; color: #4b5563; margin: 2px 0 8px; }
            .spp-pickup-meta__row--bulk { font-size: 12px; color: #6b7280; }
            .spp-pickup-meta__row--bulk a { font-family: ui-monospace, Menlo, monospace; }
            /* iPhone-height screens (iPhone 11 Safari ≈ 375×660): the sign step
               must fit without scrolling — Box line + Player Number pushed the
               Confirm button off-screen (stores, 2026-09-04). */
            @media (max-height: 760px) {
              .spp-header { padding-top: calc(8px + env(safe-area-inset-top)); padding-bottom: 8px; }
              .spp-screen { gap: 10px; padding: 10px 14px 14px; }
              .spp-pickup-meta { padding: 8px 12px; gap: 2px; font-size: 13px; }
              .spp-label { margin-top: 0; }
              .spp-input { padding: 11px 12px; }
              .spp-submit { padding: 13px 16px; font-size: 16px; }
            }
            .spp-bulk a { font-family: ui-monospace, Menlo, monospace; }
            .spp-fo-list { display: flex; flex-direction: column; gap: 10px; }
            .spp-fo-row { display: flex; align-items: flex-start; gap: 12px; background: #fff; border: 2px solid #e1e3e5; border-radius: 12px; padding: 14px; cursor: pointer; transition: border-color .12s, background .12s; }
            .spp-fo-row:hover { border-color: #c9cccf; }
            .spp-fo-row--on { background: #ecfdf5; border-color: #19a974; }
            .spp-fo-row__cb { width: 22px; height: 22px; margin-top: 3px; accent-color: #19a974; flex-shrink: 0; cursor: pointer; }
            .spp-fo-row__body { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
            .spp-fo-row__title { font-size: 13px; color: #6d7175; font-weight: 600; letter-spacing: 0.3px; }
            .spp-fo-row__items { font-size: 18px; color: #202223; }
            .spp-fo-row__items strong { font-size: 22px; }
            .spp-fo-row__players { font-size: 14px; color: #4b5563; }
            .spp-label { font-size: 13px; color: #6d7175; margin-top: 6px; }
            .spp-sig { position: relative; background: #fff; border: 1px solid #c9cccf; border-radius: 12px; height: clamp(140px, 24vh, 200px); flex-shrink: 0; }
            .spp-sig__canvas { width: 100%; height: 100%; border-radius: 12px; touch-action: none; cursor: crosshair; display: block; }
            .spp-sig__clear { position: absolute; top: 8px; right: 8px; background: #f4f6f8; color: #202223; border: 1px solid #e1e3e5; padding: 6px 10px; border-radius: 8px; font-size: 12px; cursor: pointer; }
            .spp-error { background: #fdeae7; color: #b21d10; padding: 10px 12px; border-radius: 10px; font-size: 14px; border-left: 4px solid #d72c0d; }
            .spp-submit { background: #19a974; color: #fff; border: none; padding: 16px 18px; border-radius: 12px; font-size: 17px; font-weight: 700; cursor: pointer; margin-top: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
            .spp-submit:hover { background: #128a5f; }
            .spp-submit:disabled { opacity: 0.6; cursor: not-allowed; }
            .spp-done { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 40px 16px; }
            .spp-done__icon { font-size: 84px; color: #19a974; background: #ecfdf5; border-radius: 50%; width: 140px; height: 140px; display: flex; align-items: center; justify-content: center; }
            .spp-done__title { font-size: 22px; font-weight: 700; color: #202223; }
            .spp-done__sub { color: #6d7175; font-size: 15px; }

            /* ── Console mode (sidebar entry) — light theme to match other portals ── */
            .spp-page--light { position: relative; inset: auto; background: transparent; color: #202223; }
            /* Header sits directly on the page bg (no white card chrome), matches
               .pp-page-header in PressingPortal: large title + muted subtitle. */
            .spp-page--light .spp-header { background: transparent; border-bottom: none; padding: 24px 24px 8px; padding-top: calc(24px + env(safe-area-inset-top)); }
            .spp-page--light .spp-title { font-size: 24px; font-weight: 700; color: #202223; }
            .spp-page--light .spp-page-loc { font-size: 14px; color: #6d7175; margin-top: 4px; }
            .spp-page--light .spp-store-chip { background: #f6f6f7; color: #202223; }
            .spp-page--light .spp-back { border: 1px solid #c9cccf; color: #202223; }
            .spp-page--light .spp-back-big { border: 1px solid #c9cccf; color: #202223; }
            .spp-page--light .spp-feed-empty { color: #6d7175; }
            .spp-page--light .spp-feed-item { background: #fff; border: 1px solid #e1e3e5; border-left-width: 4px; border-left-color: #c9cccf; }
            .spp-page--light .spp-feed-item__msg { color: #6d7175; }
            .spp-page--light .spp-pickup-meta { background: #f6f6f7; color: #202223; }
            .spp-page--light .spp-label { color: #6d7175; }
            .spp-page--light .spp-error { background: #fdeae7; color: #b21d10; border-left-color: #d72c0d; }
            .spp-page--light .spp-done__sub { color: #6d7175; }
            /* Inner screens drop the kiosk inset and inherit the AppShell gray bg. */
            .spp-page--light .spp-screen { padding: 0 24px 24px; max-width: 720px; margin: 0 auto; }
            /* Scan bar styled to match Pressing Portal's .os-input pill: magnifier
               icon left, camera button absolute-right, single rounded outline. */
            .spp-page--light .spp-scan-row {
              position: relative; background: #fff; border: 2px solid #c9cccf; border-radius: 12px;
              padding: 0; align-items: center; transition: border-color 0.15s;
            }
            .spp-page--light .spp-scan-row:focus-within { border-color: #1a4fa0; }
            .spp-page--light .spp-scan-row::before {
              content: '🔍'; position: absolute; left: 16px; top: 50%; transform: translateY(-50%);
              font-size: 20px; pointer-events: none;
            }
            .spp-page--light .spp-input--scan {
              border: none; background: transparent; outline: none; box-shadow: none !important;
              padding: 16px 56px 16px 50px; font-size: 18px; letter-spacing: 0;
            }
            .spp-page--light .spp-input--scan:focus {
              border: none; box-shadow: none !important;
            }
            .spp-page--light .spp-camera-btn {
              position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
              width: 36px; height: 36px; border: none; background: none; padding: 0; font-size: 22px;
              border-radius: 8px;
            }
            .spp-page--light .spp-camera-btn:hover { background: #f1f2f4; border: none; }

            /* Pickup typeahead results — match Pressing Portal's .os-result style. */
            .spp-search-results { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
            .spp-search-row {
              display: flex; align-items: center; justify-content: space-between; gap: 12px;
              background: #fff; border: 1px solid #e1e3e5; border-radius: 12px;
              padding: 14px 18px; cursor: pointer; text-align: left; width: 100%;
              box-shadow: 0 1px 3px rgba(0,0,0,.06); transition: border-color .15s;
            }
            .spp-search-row:hover { border-color: #1a4fa0; }
            .spp-search-row__name { font-weight: 700; font-size: 15px; color: #0d2240; min-width: 90px; }
            .spp-search-row__customer { flex: 1; font-size: 14px; color: #374151; }
            .spp-search-row__items { font-size: 12px; color: #6b7280; white-space: nowrap; }
            .spp-search-empty { color: #6d7175; font-size: 14px; padding: 16px; text-align: center; margin-top: 4px; }
            .spp-search-row__players { color: #6b7280; }
            .spp-search-row__status { font-size: 11px; padding: 4px 10px; border-radius: 999px; white-space: nowrap; font-weight: 600; text-transform: capitalize; background: #f1f2f4; color: #4b5563; }
            .spp-search-row__status--2 { background: #d1f5d3; color: #136f38; }
            .spp-search-row__status--3 { background: #e1e3e5; color: #6b7280; }
            .spp-search-row__status--1 { background: #fef3c7; color: #92400e; }
            .spp-error { white-space: pre-line; }
            .spp-page--light .spp-search-row { border-color: #e1e3e5; }
        `}</style>
    );
}
